import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarDays, MapPin, Pencil, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { Btn, Empty, Modal, Page, Spinner, TopBar } from "@/components/kit";
import ContactRow from "@/components/ContactRow";
import { EventForm } from "@/components/EventPicker";
import { useDeleteEvent, useEvent, useEventContacts, useRemoveFromEvent, useUpdateEvent } from "@/hooks/queries";
import { fmtDate } from "@/lib/toto";

export default function EventDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: ev, isLoading } = useEvent(id);
  const contacts = useEventContacts(id).data ?? [];
  const update = useUpdateEvent(id);
  const del = useDeleteEvent();
  const remove = useRemoveFromEvent();
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);

  if (isLoading) return <Spinner />;
  if (!ev) return <Page><TopBar title="Event" /><Empty icon={CalendarDays} title="Event not found" /></Page>;
  return (
    <Page>
      <TopBar title="Event" right={<>
        <button className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F1F5F9]" onClick={() => setEditing(true)} data-testid="event-edit"><Pencil className="w-5 h-5" /></button>
        <button className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#FEF2F2]" onClick={() => setConfirm(true)} data-testid="event-delete"><Trash2 className="w-5 h-5 text-[#DC2626]" /></button>
      </>} />
      <div className="rounded-[20px] p-5 text-white bg-gradient-to-r from-[#0000EC] to-[#2563EB] mt-2">
        <h1 className="text-[24px] font-extrabold" data-testid="event-name">{ev.name}</h1>
        <p className="mt-2 flex items-center gap-2 text-[15px] text-white/90"><CalendarDays className="w-4 h-4" />{fmtDate(ev.eventDate)}</p>
        {ev.location && <p className="mt-1 flex items-center gap-2 text-[15px] text-white/90"><MapPin className="w-4 h-4" />{ev.location}</p>}
        {ev.description && <p className="mt-3 text-[14px] text-white/85">{ev.description}</p>}
      </div>
      <h2 className="t-section mt-8 mb-3">Contacts ({contacts.length})</h2>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {contacts.map((c) => (
          <ContactRow key={c.id} contact={c} right={
            <button className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[#FEF2F2]" title="Remove from event" onClick={() => remove.mutate({ eventId: id, contactId: c.id })} data-testid={`event-remove-contact-${c.id}`}><X className="w-5 h-5 text-[#DC2626]" /></button>
          } />
        ))}
      </div>
      {!contacts.length && <Empty icon={Users} title="No contacts yet" text="Add contacts to this event from a contact's page or while reviewing a scan." />}
      <Modal open={editing} onClose={() => setEditing(false)} title="Edit Event" testid="edit-event-modal">
        <EventForm initial={ev} submitLabel="Save Changes" prefix="edit-event" loading={update.isPending}
          onSubmit={async (b) => { try { await update.mutateAsync(b); toast.success("Event updated."); setEditing(false); } catch (e) { toast.error(e.message); } }} />
      </Modal>
      <Modal open={confirm} onClose={() => setConfirm(false)} title="Delete event?" description="Contacts stay saved — only the event is removed." testid="delete-event-modal">
        <div className="grid grid-cols-2 gap-2">
          <Btn variant="ghost" onClick={() => setConfirm(false)}>Cancel</Btn>
          <Btn variant="danger" loading={del.isPending} onClick={async () => { await del.mutateAsync(id); toast.success("Event deleted."); nav("/events"); }} testid="delete-event-confirm">Delete</Btn>
        </div>
      </Modal>
    </Page>
  );
}
